import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "clear", "option"]
  static values = {
    url: String,
    delay: { type: Number, default: 180 }
  }

  connect() {
    this.activeIndex = -1
    this.timer = null
    this.abortController = null
  }

  disconnect() {
    window.clearTimeout(this.timer)
    this.abortController?.abort()
  }

  queue(event) {
    if (event.isComposing) return

    const query = this.inputTarget.value.trim()
    this.clearTarget.hidden = query.length === 0
    window.clearTimeout(this.timer)
    this.abortController?.abort()

    if (query.length < 2) {
      this.close()
      return
    }

    this.showLoading()
    this.timer = window.setTimeout(() => this.search(query), this.delayValue)
  }

  async search(query) {
    this.abortController = new AbortController()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)
      const response = await fetch(url, {
        headers: { Accept: "text/html" },
        credentials: "same-origin",
        signal: this.abortController.signal
      })
      if (!response.ok) throw new Error("Search is temporarily unavailable.")
      if (query !== this.inputTarget.value.trim()) return

      this.resultsTarget.innerHTML = await response.text()
      this.activeIndex = -1
      this.open()
    } catch (error) {
      if (error.name === "AbortError") return
      this.resultsTarget.innerHTML = `<div class="admin-global-search-message"><span class="material-symbols-rounded">error</span><div><strong>Couldn’t search</strong><p>${this.escape(error.message)}</p></div></div>`
      this.open()
    }
  }

  submit(event) {
    event.preventDefault()
    const option = this.optionTargets[this.activeIndex] || this.optionTargets[0]
    option?.click()
  }

  navigate(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.inputTarget.blur()
      return
    }

    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return
    if (this.resultsTarget.hidden) return

    event.preventDefault()
    if (event.key === "Enter") {
      const option = this.optionTargets[this.activeIndex] || this.optionTargets[0]
      option?.click()
      return
    }

    const direction = event.key === "ArrowDown" ? 1 : -1
    const count = this.optionTargets.length
    if (count === 0) return

    this.activeIndex = (this.activeIndex + direction + count) % count
    this.renderActiveOption()
  }

  activate(event) {
    this.activeIndex = this.optionTargets.indexOf(event.currentTarget)
    this.renderActiveOption()
  }

  shortcut(event) {
    if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return

    event.preventDefault()
    this.inputTarget.focus()
    this.inputTarget.select()
    this.focus()
  }

  focus() {
    if (this.inputTarget.value.trim().length >= 2 && this.resultsTarget.innerHTML.trim()) this.open()
  }

  outside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  clear() {
    this.inputTarget.value = ""
    this.clearTarget.hidden = true
    this.abortController?.abort()
    this.close()
    this.inputTarget.focus()
  }

  open() {
    this.resultsTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    this.resultsTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
    this.renderActiveOption()
  }

  showLoading() {
    this.resultsTarget.innerHTML = '<div class="admin-global-search-loading"><span></span><span></span><span></span><em>Searching…</em></div>'
    this.open()
  }

  renderActiveOption() {
    this.optionTargets.forEach((option, index) => {
      const active = index === this.activeIndex
      option.classList.toggle("is-active", active)
      option.setAttribute("aria-selected", active ? "true" : "false")
      if (active) {
        this.inputTarget.setAttribute("aria-activedescendant", option.id)
        option.scrollIntoView({ block: "nearest" })
      }
    })
    if (this.activeIndex < 0) this.inputTarget.removeAttribute("aria-activedescendant")
  }

  escape(value) {
    const node = document.createElement("span")
    node.textContent = value
    return node.innerHTML
  }
}
